import { Injectable } from '@angular/core';
import { DecimalPipe, getCurrencySymbol } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { CountryISO } from 'ngx-intl-tel-input';

export interface CurrencyConfig {
  currencySymbol: string;
  thousandSeparator: string;
  decimalSeparator: string;
  currencyCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CurrencyFormatterService {
  private defaultConfig: CurrencyConfig = {
    currencySymbol: '₦',
    thousandSeparator: ',',
    decimalSeparator: '.',
    currencyCode: 'NGN',
  };

  private currentConfig: CurrencyConfig = this.defaultConfig;
  private symbolRegistry: Record<string, string> = {};

  registerSymbols(countryList: any[]): void {
    for (const country of countryList) {
      if (country.currencyCode && country.currencySymbol) {
        this.symbolRegistry[country.currencyCode] = country.currencySymbol;
      }
    }
  }

  public tenantCountryISO$ = new BehaviorSubject<CountryISO>(
    CountryISO.Nigeria,
  );

  get tenantCountryISO(): CountryISO {
    return this.tenantCountryISO$.value;
  }

  constructor(private decimalPipe: DecimalPipe) {}

  /**
   * Set the currency configuration based on tenant data
   */
  setCurrencyConfig(tenantData: any): void {
    if (tenantData?.tenantCountry) {
      this.currentConfig = {
        currencySymbol: tenantData.tenantCountry.currencySymbol || '₦',
        thousandSeparator: this.getSeparatorChar(
          tenantData.tenantCountry.thousandSeparator,
        ),
        decimalSeparator: this.getSeparatorChar(
          tenantData.tenantCountry.decimalSeparator,
        ),
        currencyCode: tenantData.tenantCountry.currencyCode || 'NGN',
      };

      const iso = tenantData.tenantCountry.isoCode?.toLowerCase();
      if (iso) this.tenantCountryISO$.next(iso as CountryISO);
    } else {
      this.currentConfig = this.defaultConfig;
      this.tenantCountryISO$.next(CountryISO.Nigeria);
    }
  }

  /**
   * Convert separator string to character
   */
  private getSeparatorChar(separator: string): string {
    switch (separator) {
      case 'COMMA':
        return ',';
      case 'PERIOD':
        return '.';
      case 'SPACE':
        return ' ';
      case 'APOSTROPHE':
        return "'";
      default:
        return separator || ',';
    }
  }

  /**
   * Get current currency configuration
   */
  getConfig(): CurrencyConfig {
    return { ...this.currentConfig };
  }

  /**
   * Format a number as currency with tenant-specific formatting
   */
  formatCurrency(
    value: number | string,
    showSymbol: boolean = true,
    decimals: number = 2,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return '-';
    }

    if (numValue === 0) {
      return '-';
    }

    // Format using Angular's DecimalPipe for proper decimal handling
    const formattedNumber = this.decimalPipe.transform(
      numValue,
      `1.${decimals}-${decimals}`,
    );

    if (!formattedNumber) {
      return '-';
    }

    let result = formattedNumber;

    // Split the number into integer and fractional parts based on the DecimalPipe's default decimal separator (period)
    const parts = result.split('.');
    let integerPart = parts[0];
    let fractionalPart = parts.length > 1 ? parts[1] : '';

    // Replace the thousand separator in the integer part
    // Angular's DecimalPipe uses comma as default thousand separator
    if (this.currentConfig.thousandSeparator !== ',') {
      integerPart = integerPart.replace(
        /,/g,
        this.currentConfig.thousandSeparator,
      );
    }

    // Reconstruct the number with the correct decimal separator
    if (fractionalPart) {
      result =
        integerPart + this.currentConfig.decimalSeparator + fractionalPart;
    } else {
      result = integerPart;
    }

    // Add currency symbol
    if (showSymbol) {
      result = `${this.currentConfig.currencySymbol}${result}`;
    }

    return result;
  }

  /**
   * Format a number without currency symbol (just number formatting)
   */
  formatNumber(value: number | string, decimals: number = 2): string {
    return this.formatCurrency(value, false, decimals);
  }

  /**
   * Format a number as percentage with tenant-specific decimal separator
   */
  formatPercentage(value: number | string, decimals: number = 1): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return '-';
    }

    if (numValue === 0) {
      return '0%';
    }

    // Format using Angular's DecimalPipe for proper decimal handling
    const formattedNumber = this.decimalPipe.transform(
      numValue,
      `1.${decimals}-${decimals}`,
    );

    if (!formattedNumber) {
      return '-';
    }

    let result = formattedNumber;

    // Split the number into integer and fractional parts based on the DecimalPipe's default decimal separator (period)
    const parts = result.split('.');
    let integerPart = parts[0];
    let fractionalPart = parts.length > 1 ? parts[1] : '';

    // Reconstruct the number with the correct decimal separator (no thousand separator for percentages)
    if (fractionalPart) {
      result =
        integerPart + this.currentConfig.decimalSeparator + fractionalPart;
    } else {
      result = integerPart;
    }

    // Add percentage symbol
    result = result + '%';

    return result;
  }

  /**
   * Parse a formatted currency string back to number
   */
  parseCurrency(formattedValue: string): number {
    if (!formattedValue) return 0;

    // Remove currency symbol
    let cleanValue = formattedValue.replace(
      this.currentConfig.currencySymbol,
      '',
    );

    // Replace custom separators with standard JavaScript separators
    cleanValue = cleanValue.replace(
      new RegExp(`\\${this.currentConfig.thousandSeparator}`, 'g'),
      '',
    );
    cleanValue = cleanValue.replace(
      new RegExp(`\\${this.currentConfig.decimalSeparator}`, 'g'),
      '.',
    );

    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Format currency for input fields (allows editing)
   */
  formatForInput(value: number | string): string {
    return this.formatNumber(value, 2);
  }

  /**
   * Parse input value and return as number
   */
  parseInput(value: string): number {
    return this.parseCurrency(value);
  }

  /**
   * Get currency symbol only
   */
  getCurrencySymbol(): string {
    return this.currentConfig.currencySymbol;
  }

  /**
   * Get the tenant's ISO currency code (NGN, GHS, ...), for payloads that
   * carry the code rather than the symbol.
   */
  getCurrencyCode(): string {
    return this.currentConfig.currencyCode ?? 'NGN';
  }

  /**
   * Returns the narrow currency symbol for any ISO currency code.
   * Falls back to the code itself if Angular doesn't recognise it.
   */
  getSymbolForCode(code: string): string {
    if (!code) return this.getCurrencySymbol();
    if (code === this.currentConfig.currencyCode) return this.getCurrencySymbol();
    if (this.symbolRegistry[code]) return this.symbolRegistry[code];
    try {
      return getCurrencySymbol(code, 'narrow');
    } catch {
      return code;
    }
  }

  /**
   * Reset to default configuration
   */
  resetToDefault(): void {
    this.currentConfig = { ...this.defaultConfig };
  }
}
